import React, { useEffect, useMemo, useState } from 'react';
import {
  FiSearch, FiCopy, FiCheck, FiChevronDown, FiChevronRight,
  FiBookOpen, FiArrowLeft, FiExternalLink, FiGrid,
} from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import Modal from '../components/Modal/Modal';
import CatalogJsonViewer from '../components/CatalogJsonViewer/CatalogJsonViewer';
import s from './Landing.module.css';

const SCOPES = [
  { key: 'fictive', label: 'Fictive Matches', api: 'fictive' },
  { key: 'real', label: 'Real Matches', api: 'real' },
  { key: 'manual', label: 'Manual Markets', api: 'manual' },
];

function CodeBadge({ code }) {
  const [copied, setCopied] = useState(false);
  const copy = (e) => {
    e.stopPropagation();
    navigator.clipboard?.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    });
  };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      fontSize: '0.78rem',
      padding: '3px 8px',
      background: 'rgba(108, 140, 191, 0.15)',
      border: '1px solid rgba(108, 140, 191, 0.35)',
      borderRadius: 6,
      color: '#a8c1e8',
    }}>
      {code}
      <button
        onClick={copy}
        title="Copy code"
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: copied ? '#73c991' : '#8aa3c7', padding: 0,
          display: 'inline-flex', alignItems: 'center',
        }}
      >
        {copied ? <FiCheck size={12} /> : <FiCopy size={12} />}
      </button>
    </span>
  );
}

function Pill({ children, color = 'rgba(255,255,255,0.06)', text = 'var(--text-secondary)' }) {
  return (
    <span style={{
      fontSize: '0.7rem',
      padding: '2px 8px',
      borderRadius: 999,
      background: color,
      color: text,
      border: '1px solid rgba(255,255,255,0.08)',
      whiteSpace: 'nowrap',
    }}>{children}</span>
  );
}

function MarketRow({ m }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{
      borderBottom: '1px solid rgba(255,255,255,0.05)',
      padding: '12px 0',
    }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer',
          flexWrap: 'wrap',
        }}
      >
        {open ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
        <CodeBadge code={m.code} />
        <span style={{ fontSize: '0.88rem', color: 'var(--text-primary)', flex: 1, minWidth: 240 }}>
          {m.name}
        </span>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {m.periodName && <Pill>{m.periodName}</Pill>}
          {m.statKindName && <Pill>{m.statKindName}</Pill>}
          {m.outcomeName && <Pill>{m.outcomeName}</Pill>}
          {m.line != null && <Pill>line {m.line}</Pill>}
          {m.handicap && <Pill>h {m.handicap}</Pill>}
        </div>
      </div>
      {open && (
        <div style={{
          marginTop: 10, marginLeft: 26, fontSize: '0.82rem',
          color: 'var(--text-secondary)', lineHeight: 1.55,
        }}>
          <div style={{ marginBottom: 6 }}>{m.description}</div>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: '0.74rem', color: 'var(--text-muted)' }}>
            {m.column && <span>column: <code>{m.column}</code></span>}
            {m.valueType && <span>type: {m.valueType}</span>}
            {m.side && <span>side: {m.side}</span>}
            {m.applicableTo?.length > 0 && <span>applies to: {m.applicableTo.join(', ')}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

function GroupAccordion({ group, markets, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div style={{
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: 10,
      marginBottom: 10,
      background: 'rgba(255,255,255,0.02)',
    }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          padding: '12px 16px', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {open ? <FiChevronDown /> : <FiChevronRight />}
          <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-primary)' }}>
            {group}
          </h3>
        </div>
        <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{markets.length} markets</span>
      </div>
      {open && (
        <div style={{ padding: '0 16px 12px' }}>
          {markets.map(m => <MarketRow key={m.code} m={m} />)}
        </div>
      )}
    </div>
  );
}

function ManualMarketsBlock({ block }) {
  if (!block) return null;
  return (
    <div style={{
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: 10,
      padding: 20,
      background: 'rgba(255,255,255,0.02)',
    }}>
      <h3 style={{ margin: '0 0 8px', color: 'var(--text-primary)', fontSize: '1rem' }}>{block.name}</h3>
      <p style={{ margin: '0 0 16px', color: 'var(--text-secondary)', fontSize: '0.88rem', lineHeight: 1.55 }}>
        {block.description}
      </p>

      <h4 style={{ margin: '16px 0 8px', fontSize: '0.85rem', color: 'var(--text-primary)' }}>Endpoints</h4>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {Object.entries(block.endpoints || {}).map(([k, v]) => (
          <div key={k} style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
            <strong style={{ color: 'var(--text-primary)' }}>{k}:</strong>{' '}
            <code style={{ color: '#a8c1e8' }}>{v}</code>
          </div>
        ))}
      </div>

      <h4 style={{ margin: '20px 0 8px', fontSize: '0.85rem', color: 'var(--text-primary)' }}>Fields</h4>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              <th style={{ textAlign: 'left', padding: '8px 10px', color: 'var(--text-muted)', fontWeight: 500 }}>Code</th>
              <th style={{ textAlign: 'left', padding: '8px 10px', color: 'var(--text-muted)', fontWeight: 500 }}>Type</th>
              <th style={{ textAlign: 'left', padding: '8px 10px', color: 'var(--text-muted)', fontWeight: 500 }}>Description</th>
            </tr>
          </thead>
          <tbody>
            {(block.fields || []).map(f => (
              <tr key={f.code} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                <td style={{ padding: '8px 10px' }}><code style={{ color: '#a8c1e8' }}>{f.code}</code></td>
                <td style={{ padding: '8px 10px', color: 'var(--text-secondary)' }}>{f.type}</td>
                <td style={{ padding: '8px 10px', color: 'var(--text-secondary)' }}>{f.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GlossaryPanel({ glossary }) {
  if (!glossary) return null;
  const entries = Object.entries(glossary);
  if (!entries.length) return null;
  return (
    <div style={{
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: 10,
      padding: 16,
      background: 'rgba(255,255,255,0.02)',
      position: 'sticky', top: 24, alignSelf: 'flex-start',
    }}>
      <h3 style={{ margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.95rem' }}>
        <FiBookOpen /> Glossary
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {entries.map(([term, def]) => (
          <div key={term}>
            <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-primary)' }}>{term}</div>
            <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>{def}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function MarketsCatalog() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [scopeKey, setScopeKey] = useState('fictive');
  const [search, setSearch] = useState('');
  const [periodFilter, setPeriodFilter] = useState(null);
  const [statFilter, setStatFilter] = useState(null);

  // Single market lookup
  const [lookupCode, setLookupCode] = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupErr, setLookupErr] = useState(null);

  // JSON workbook viewer modal
  const [jsonOpen, setJsonOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/v1/markets-catalog/`);
        if (!res.ok) throw new Error(`Failed to load catalog (${res.status})`);
        const json = await res.json();
        if (mounted) setData(json);
      } catch (e) {
        if (mounted) setErr(e.message);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const currentSection = useMemo(() => {
    if (!data) return null;
    if (scopeKey === 'manual') return null;
    const scopeUpper = scopeKey.toUpperCase();
    return (data.sections || []).find(sec => sec.scope === scopeUpper);
  }, [data, scopeKey]);

  const periods = useMemo(() => {
    if (!currentSection) return [];
    const set = new Map();
    currentSection.markets.forEach(m => {
      if (m.period) set.set(m.period, m.periodName || m.period);
    });
    return Array.from(set.entries());
  }, [currentSection]);

  const statKinds = useMemo(() => {
    if (!currentSection) return [];
    const set = new Map();
    currentSection.markets.forEach(m => {
      if (m.statKind) set.set(m.statKind, m.statKindName || m.statKind);
    });
    return Array.from(set.entries());
  }, [currentSection]);

  const filteredMarkets = useMemo(() => {
    if (!currentSection) return [];
    const q = search.trim().toLowerCase();
    return currentSection.markets.filter(m => {
      if (periodFilter && m.period !== periodFilter) return false;
      if (statFilter && m.statKind !== statFilter) return false;
      if (q) {
        const hay = `${m.code} ${m.name} ${m.description || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [currentSection, search, periodFilter, statFilter]);

  const grouped = useMemo(() => {
    const map = new Map();
    filteredMarkets.forEach(m => {
      const key = m.groupName || m.group || 'Other';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(m);
    });
    return Array.from(map.entries());
  }, [filteredMarkets]);

  const handleLookup = async () => {
    const code = lookupCode.trim();
    if (!code) return;
    setLookupErr(null);
    setLookupResult(null);
    try {
      const res = await fetch(`${API_BASE_URL}/v1/markets-catalog/code/${encodeURIComponent(code)}`);
      if (res.status === 404) { setLookupErr(`No market found with code "${code}".`); return; }
      if (!res.ok) throw new Error(`Lookup failed (${res.status})`);
      const json = await res.json();
      setLookupResult(json);
    } catch (e) {
      setLookupErr(e.message);
    }
  };

  return (
    <div className={s.landing} style={{ minHeight: '100vh' }}>
      {/* Top bar */}
      <div className={s.topBar}>
        <div className={s.logo}>
          <img className={s.logoImg} src={process.env.PUBLIC_URL + '/clasico_analytics.jpeg'} alt="Logo" />
          <span className={s.logoText}>Clasico Analytics</span>
        </div>
        <Link to="/" className={s.authBtn} style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <FiArrowLeft /> Back to home
        </Link>
      </div>

      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '32px 24px 80px' }}>
        {/* Header */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 8 }}>
            Public Integrator Documentation
          </div>
          <h1 style={{ fontSize: '2rem', margin: '0 0 8px', color: 'var(--text-primary)' }}>
            Markets Catalog
          </h1>
          <p style={{ color: 'var(--text-secondary)', maxWidth: 760, lineHeight: 1.55 }}>
            Stable, machine-readable reference for every market our pricing engine exposes —
            ready for partner integration. Backed by{' '}
            <code style={{ color: '#a8c1e8' }}>/api/v1/markets-catalog/</code>{' '}
            (public, no auth required).
          </p>
          {data && (
            <div style={{ display: 'flex', gap: 16, marginTop: 14, fontSize: '0.8rem', color: 'var(--text-muted)', flexWrap: 'wrap', alignItems: 'center' }}>
              <span>Product: <strong style={{ color: 'var(--text-primary)' }}>{data.productName}</strong></span>
              <span>Version: <strong style={{ color: 'var(--text-primary)' }}>{data.version}</strong></span>
              <span>Last updated: <strong style={{ color: 'var(--text-primary)' }}>{new Date(data.generatedAt).toLocaleString()}</strong></span>
              <button
                type="button"
                onClick={() => setJsonOpen(true)}
                style={{
                  background: 'var(--accent)',
                  color: '#0a0f1c',
                  border: 'none',
                  borderRadius: 6,
                  padding: '6px 12px',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <FiGrid size={12} /> View JSON workbook
              </button>
              <a
                href={`${API_BASE_URL}/v1/markets-catalog/json`}
                target="_blank" rel="noreferrer"
                style={{ color: 'var(--text-muted)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                Raw JSON <FiExternalLink size={12} />
              </a>
            </div>
          )}
        </div>

        {loading && <div style={{ color: 'var(--text-muted)' }}>Loading catalog…</div>}
        {err && <div style={{ color: '#e06c75' }}>{err}</div>}

        {data && (
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 280px', gap: 24, alignItems: 'start' }}>
            <div>
              {/* Code lookup */}
              <div style={{
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 10,
                padding: 16,
                marginBottom: 20,
                background: 'rgba(255,255,255,0.02)',
              }}>
                <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 10 }}>
                  Lookup market by code
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <input
                    placeholder="e.g. ttht05o"
                    value={lookupCode}
                    onChange={(e) => setLookupCode(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleLookup(); }}
                    style={{
                      flex: 1, minWidth: 200,
                      padding: '8px 12px', borderRadius: 8,
                      background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)',
                      color: 'var(--text-primary)', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                      fontSize: '0.85rem', outline: 'none',
                    }}
                  />
                  <button onClick={handleLookup} className={s.authBtn}>Lookup</button>
                </div>
                {lookupErr && <div style={{ marginTop: 10, color: '#e06c75', fontSize: '0.82rem' }}>{lookupErr}</div>}
                {lookupResult && (
                  <div style={{ marginTop: 14 }}>
                    <MarketRow m={lookupResult} />
                  </div>
                )}
              </div>

              {/* Scope tabs */}
              <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid rgba(255,255,255,0.1)', marginBottom: 16 }}>
                {SCOPES.map(sc => (
                  <button
                    key={sc.key}
                    onClick={() => { setScopeKey(sc.key); setPeriodFilter(null); setStatFilter(null); }}
                    style={{
                      padding: '10px 18px', background: 'none',
                      border: 'none',
                      borderBottom: `2px solid ${scopeKey === sc.key ? 'var(--accent)' : 'transparent'}`,
                      color: scopeKey === sc.key ? 'var(--accent)' : 'var(--text-secondary)',
                      fontSize: '0.88rem', fontWeight: 500, cursor: 'pointer',
                    }}
                  >
                    {sc.label}
                  </button>
                ))}
              </div>

              {scopeKey === 'manual' ? (
                <ManualMarketsBlock block={data.manualMarkets} />
              ) : (
                <>
                  {/* Toolbar */}
                  <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
                    <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
                      <FiSearch style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                      <input
                        placeholder="Search by code, name or description…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{
                          width: '100%', padding: '8px 12px 8px 32px', borderRadius: 8,
                          background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)',
                          color: 'var(--text-primary)', fontSize: '0.85rem', outline: 'none',
                        }}
                      />
                    </div>
                    <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                      {filteredMarkets.length} of {currentSection?.markets.length || 0} markets
                    </span>
                  </div>

                  {/* Filter chips */}
                  {(periods.length > 0 || statKinds.length > 0) && (
                    <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
                      {periods.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>Period:</span>
                          <Chip active={!periodFilter} onClick={() => setPeriodFilter(null)}>All</Chip>
                          {periods.map(([k, n]) => (
                            <Chip key={k} active={periodFilter === k} onClick={() => setPeriodFilter(k)}>{n}</Chip>
                          ))}
                        </div>
                      )}
                      {statKinds.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>Stat:</span>
                          <Chip active={!statFilter} onClick={() => setStatFilter(null)}>All</Chip>
                          {statKinds.map(([k, n]) => (
                            <Chip key={k} active={statFilter === k} onClick={() => setStatFilter(k)}>{n}</Chip>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {currentSection?.description && (
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.82rem', marginBottom: 16, lineHeight: 1.5 }}>
                      {currentSection.description}
                    </p>
                  )}

                  {grouped.length === 0 ? (
                    <div style={{ color: 'var(--text-muted)', padding: 20, textAlign: 'center' }}>
                      No markets match your filters.
                    </div>
                  ) : grouped.map(([g, ms], i) => (
                    <GroupAccordion key={g} group={g} markets={ms} defaultOpen={i === 0} />
                  ))}
                </>
              )}
            </div>

            {/* Glossary side panel */}
            <GlossaryPanel glossary={data.glossary} />
          </div>
        )}
      </div>

      <Modal open={jsonOpen} onClose={() => setJsonOpen(false)} title="Markets Catalog — JSON Workbook" wide>
        <CatalogJsonViewer scope="all" height="70vh" />
      </Modal>
    </div>
  );
}

function Chip({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '4px 10px', borderRadius: 999,
        background: active ? 'rgba(108, 140, 191, 0.25)' : 'transparent',
        border: `1px solid ${active ? 'rgba(108, 140, 191, 0.6)' : 'rgba(255,255,255,0.1)'}`,
        color: active ? '#cddcf1' : 'var(--text-secondary)',
        fontSize: '0.74rem', cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}
